import classNames from "classnames"
import React from "react"

import { Label2, Paragraph1, Title5 } from "@bounce/design-system"

import { Link } from "../../../Navigation/Link/Link"
import type { PNImageProps } from "../../PNImage/PNImage"
import { PNImage } from "../../PNImage/PNImage"
import { isFullPostAuthor } from "../PostAuthor/helpers"
import type { PostAuthorProps } from "../PostAuthor/PostAuthor"
import { PostAuthor } from "../PostAuthor/PostAuthor"

export type PostCardProps = {
  title: string
  titleLevel?: 3 | 4
  excerpt?: string
  date?: string
  href: string
  imgixParams?: PNImageProps["imgixParams"]
  imageLoading?: PNImageProps["loading"]
  image?: PNImageProps["src"]
  author?: Pick<PostAuthorProps, "href" | "name" | "metadata" | "image">
  size?: "xs" | "sm"
  className?: string
}

export const PostCard = ({
  image,
  excerpt,
  date,
  title,
  href,
  className,
  size = "sm",
  imageLoading = "lazy",
  imgixParams,
  titleLevel = 3,
  author,
}: PostCardProps) => {
  const hasExcerpt = !!excerpt
  const hasAuthor = isFullPostAuthor(author)
  const isSm = size === "sm"

  return (
    <article className={classNames("flex h-full flex-col justify-between border-b border-neutral-300", className)}>
      <Link href={href} title={title} className={classNames(!hasAuthor && "pb-10 md:pb-16")}>
        {!!image && (
          <PNImage
            data-chromatic="ignore"
            src={image}
            imgixParams={{ width: 580, ...imgixParams }}
            width={580}
            height={214}
            loading={imageLoading}
            unoptimized={imageLoading === "lazy"}
            className={classNames(
              "h-[192px] w-full bg-neutral-100 object-cover object-center md:h-[214px]",
              isSm && "lg:h-[362px]",
            )}
          />
        )}
        <div className={classNames("flex w-full flex-1 flex-col", "text-black", !!image ? "pt-8" : "pt-2")}>
          {!!date && (
            <Label2 as="span" className={["mb-2", isSm && "lg:mb-3"]}>
              <span suppressHydrationWarning>{date}</span>
            </Label2>
          )}
          <Title5 level={titleLevel} numberOfLines={3}>
            {title}
          </Title5>
          {hasExcerpt && (
            <Paragraph1 numberOfLines={2} className="mt-2 md:mt-3">
              {excerpt}
            </Paragraph1>
          )}
        </div>
      </Link>
      {hasAuthor && <PostAuthor {...author} imageLoading={imageLoading} className="mb-6 mt-8" />}
    </article>
  )
}
